import { styled } from "goober";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { chatLabel, pickChatIconColor } from "./pickChatIconColor";

export const ModalContent = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
`;

export const ModalTitleBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ModalList = styled("div")`
  margin: 0;
  padding: 4px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  background: var(--surface-secondary);
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-sizing: border-box;
`;

const RowButton = styled(PressButton)`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }

  & .arrow-icon {
    opacity: 0;
    flex-shrink: 0;
    display: flex;
    margin-left: auto;
  }

  &:hover .arrow-icon {
    opacity: 0.5;
  }
`;

const RowStatic = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 8px;
  min-width: 0;
`;

const SelectRow = styled(PressButton)`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }
`;

export const ModalBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ModalFooter = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

type RowProps = {
  id: string;
  name: string;
  onClick?: (() => void) | undefined;
  showArrow?: boolean;
};

export function PersonalChatListRow({
  id,
  name,
  onClick,
  showArrow,
}: RowProps) {
  const color = pickChatIconColor(id);
  const label = chatLabel(name);
  const body = (
    <>
      <Icon
        name="ask"
        size={16}
        color={`var(--${color}-600)`}
        style={{ flexShrink: 0 }}
      />
      <Text
        variant="text-regular"
        color="var(--text-primary)"
        ellipsis
        style={{ flex: "1 1 auto", minWidth: 0 }}
      >
        {label}
      </Text>
      {showArrow && (
        <span className="arrow-icon">
          <Icon name="arrow-up-right" size={12} />
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <RowButton data-no-pill="" onPress={onClick}>
        {body}
      </RowButton>
    );
  }

  return <RowStatic>{body}</RowStatic>;
}

type SelectableRowProps = {
  id: string;
  name: string;
  selected: boolean;
  onToggle: () => void;
};

export function PersonalChatSelectableListRow({
  id,
  name,
  selected,
  onToggle,
}: SelectableRowProps) {
  const color = pickChatIconColor(id);
  const label = chatLabel(name);

  return (
    <SelectRow data-no-pill="" onPress={onToggle} aria-pressed={selected}>
      <Icon
        name="ask"
        size={16}
        color={`var(--${color}-600)`}
        style={{ flexShrink: 0 }}
      />
      <Text
        variant="text-regular"
        color="var(--text-primary)"
        ellipsis
        style={{ flex: "1 1 auto", minWidth: 0 }}
      >
        {label}
      </Text>
      <span
        style={{
          display: "inline-flex",
          marginLeft: "auto",
          opacity: selected ? 1 : 0.12,
        }}
      >
        <Icon name={selected ? "check-filled" : "check-empty"} size={16} />
      </span>
    </SelectRow>
  );
}
